import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import Icon from '../components/Icon';
import PremiumSelect from '../components/PremiumSelect';
import CustomDatePicker from '../components/CustomDatePicker';
import { sendPushNotification, getScheduledPush, cancelScheduledPush } from '../api';

const PUSH_FORM_STORAGE_KEY = 'salut-admin-push-draft-v2';

// 30-минутная сетка: 00:00, 00:30, ... 23:30
const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 0; h < 24; h += 1) {
    for (const m of [0, 30]) {
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return slots;
})();

const pad2 = (n) => String(n).padStart(2, '0');

// Фиксированный часовой пояс проекта: выбранное время трактуется как местное
// кишинёвское, независимо от пояса браузера админа.
const PROJECT_TZ = 'Europe/Chisinau';

const tzParts = (date, timeZone) => {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const p = {};
  for (const part of dtf.formatToParts(date)) p[part.type] = part.value;
  return p;
};

// Смещение (в минутах) пояса timeZone для конкретного инстанта.
const tzOffsetMinutes = (date, timeZone) => {
  const p = tzParts(date, timeZone);
  const hour = p.hour === '24' ? 0 : Number(p.hour);
  const asUTC = Date.UTC(
    Number(p.year), Number(p.month) - 1, Number(p.day), hour, Number(p.minute), Number(p.second)
  );
  return Math.round((asUTC - date.getTime()) / 60000);
};

// Стенные дата+время в PROJECT_TZ -> инстант (Date), с учётом DST.
const projectWallToInstant = (dateStr, timeStr) => {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = timeStr.split(':').map(Number);
  const utcGuess = Date.UTC(y, mo - 1, d, h, mi, 0);
  const offset1 = tzOffsetMinutes(new Date(utcGuess), PROJECT_TZ);
  let instant = utcGuess - offset1 * 60000;
  const offset2 = tzOffsetMinutes(new Date(instant), PROJECT_TZ);
  if (offset2 !== offset1) instant = utcGuess - offset2 * 60000;
  return new Date(instant);
};

// Стенные дата+время в PROJECT_TZ -> ISO с явным offset пояса (напр. 2026-07-14T15:30:00+03:00).
const projectWallToIso = (dateStr, timeStr) => {
  const instant = projectWallToInstant(dateStr, timeStr);
  const offset = tzOffsetMinutes(instant, PROJECT_TZ);
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  return `${dateStr}T${timeStr}:00${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
};

// Сегодняшняя дата в PROJECT_TZ (YYYY-MM-DD).
const projectTodayStr = () => {
  const p = tzParts(new Date(), PROJECT_TZ);
  return `${p.year}-${p.month}-${p.day}`;
};

// Отображение запланированного времени всегда в поясе проекта.
const formatScheduledAt = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    timeZone: PROJECT_TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Краткое читаемое описание из сохранённого requestJson (для списка запланированных).
const summarizeRequestJson = (raw) => {
  if (typeof raw !== 'string' || !raw.trim()) return { title: '', description: '' };
  try {
    const parsed = JSON.parse(raw);
    const pick = (v) =>
      v && typeof v === 'object' ? (v.ru || v.ro || v.en || '') : typeof v === 'string' ? v : '';
    return { title: pick(parsed.title), description: pick(parsed.description) };
  } catch {
    return { title: '', description: '' };
  }
};

const LANGS = [
  { code: 'ru', label: 'RU' },
  { code: 'ro', label: 'RO' },
  { code: 'en', label: 'EN' }
];

const emptyLocalized = () => ({ ru: '', ro: '', en: '' });

const DEFAULT_FORM = {
  title: emptyLocalized(),
  description: emptyLocalized(),
  imageUrl: '',
  targetType: 'FILTERS',
  role: 'CUSTOMER',
  platform: ['ANDROID', 'IOS'],
  screen: 'CLIENT_PENDING_SERVICES',
  saveToHistory: false,
  profileIds: ''
};

const CLIENT_SCREEN_VALUES = new Set([
  'CLIENT_PENDING_SERVICES',
  'CLIENT_ACTIVE_SERVICES',
  'CLIENT_COMPLETED_SERVICES',
  'CLIENT_NEW_REQUEST',
  'CLIENT_NOTIFICATIONS',
  'CLIENT_PROFILE',
  'CLIENT_SETTINGS'
]);

const MASTER_SCREEN_VALUES = new Set([
  'MASTER_PURCHASED_SERVICES',
  'MASTER_ACTIVE_SERVICES',
  'MASTER_COMPLETED_SERVICES',
  'MASTER_MARKETPLACE',
  'MASTER_WALLET',
  'MASTER_CARDS',
  'MASTER_PROFILE',
  'MASTER_PORTFOLIO',
  'MASTER_REVIEWS'
]);

function loadDraftFromStorage() {
  try {
    const raw = localStorage.getItem(PUSH_FORM_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

function normalizeLocalized(raw) {
  const loc = emptyLocalized();
  if (raw && typeof raw === 'object') {
    for (const { code } of LANGS) {
      if (typeof raw[code] === 'string') loc[code] = raw[code];
    }
  } else if (typeof raw === 'string') {
    // migrate legacy single-string drafts into the Russian field
    loc.ru = raw;
  }
  return loc;
}

function normalizeDraft(raw) {
  const d = { ...DEFAULT_FORM };
  d.title = normalizeLocalized(raw.title);
  d.description = normalizeLocalized(raw.description);
  if (typeof raw.imageUrl === 'string') d.imageUrl = raw.imageUrl;
  if (typeof raw.profileIds === 'string') d.profileIds = raw.profileIds;
  if (raw.targetType === 'FILTERS' || raw.targetType === 'USERS') d.targetType = raw.targetType;
  if (raw.role === 'CUSTOMER' || raw.role === 'PROVIDER') d.role = raw.role;
  if (raw.role === 'MASTER') d.role = 'PROVIDER';
  // saveToHistory намеренно не восстанавливаем из черновика: тумблер всегда
  // стартует выключенным.
  if (Array.isArray(raw.platform)) {
    const next = raw.platform.filter((p) => p === 'ANDROID' || p === 'IOS');
    if (next.length > 0) d.platform = next;
  }
  if (typeof raw.screen === 'string') {
    if (d.role === 'CUSTOMER' && CLIENT_SCREEN_VALUES.has(raw.screen)) d.screen = raw.screen;
    else if (d.role === 'PROVIDER' && MASTER_SCREEN_VALUES.has(raw.screen)) d.screen = raw.screen;
  }
  if (d.role === 'CUSTOMER' && !CLIENT_SCREEN_VALUES.has(d.screen)) d.screen = 'CLIENT_PENDING_SERVICES';
  if (d.role === 'PROVIDER' && !MASTER_SCREEN_VALUES.has(d.screen)) d.screen = 'MASTER_MARKETPLACE';
  return d;
}

const AdminPushForm = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '', data: null });
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveToDatabaseChoice, setSaveToDatabaseChoice] = useState(false);
  const [profileIdsExpanded, setProfileIdsExpanded] = useState(false);
  const [activeLang, setActiveLang] = useState('ru');
  const [copiedFailed, setCopiedFailed] = useState(false);

  // Планирование отправки
  const [sendMode, setSendMode] = useState('now'); // 'now' | 'schedule'
  const [scheduleDate, setScheduleDate] = useState(() => projectTodayStr());
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduledSlots, setScheduledSlots] = useState([]); // ISO-инстанты
  const [scheduleError, setScheduleError] = useState('');

  // Список запланированных уведомлений
  const [scheduledItems, setScheduledItems] = useState([]);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [scheduledListError, setScheduledListError] = useState('');
  const [cancellingId, setCancellingId] = useState(null);

  const [form, setForm] = useState(() => {
    const draft = loadDraftFromStorage();
    return draft ? normalizeDraft(draft) : { ...DEFAULT_FORM };
  });

  const todayStr = projectTodayStr();

  // Стабильная ссылка на актуальный t, чтобы loadScheduled не пересоздавался
  // (иначе смена t → новый loadScheduled → лишний запрос списка).
  const tRef = React.useRef(t);
  tRef.current = t;

  const loadScheduled = React.useCallback(async () => {
    setScheduledLoading(true);
    setScheduledListError('');
    try {
      const res = await getScheduledPush();
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      // Показываем ближайшие сверху
      list.sort((a, b) => new Date(a?.scheduledAt || 0) - new Date(b?.scheduledAt || 0));
      setScheduledItems(list);
    } catch (err) {
      setScheduledListError(err.message || tRef.current('admin.notifications.scheduledList.loadError'));
    } finally {
      setScheduledLoading(false);
    }
  }, []);

  // Начальная загрузка ровно один раз. Гард через ref гасит и повторный
  // вызов из-за двойного маунта React.StrictMode в dev-режиме.
  const didInitRef = React.useRef(false);
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadScheduled();
  }, [loadScheduled]);

  const addScheduleSlot = () => {
    setScheduleError('');
    if (!scheduleDate || !scheduleTime) {
      setScheduleError(t('admin.notifications.schedule.pickDateTime'));
      return;
    }
    const iso = projectWallToIso(scheduleDate, scheduleTime);
    if (new Date(iso).getTime() <= Date.now()) {
      setScheduleError(t('admin.notifications.schedule.pastError'));
      return;
    }
    if (scheduledSlots.includes(iso)) {
      setScheduleError(t('admin.notifications.schedule.duplicate'));
      return;
    }
    setScheduledSlots((prev) => [...prev, iso].sort((a, b) => new Date(a) - new Date(b)));
    setScheduleTime('');
  };

  const removeScheduleSlot = (iso) => {
    setScheduledSlots((prev) => prev.filter((s) => s !== iso));
  };

  const handleCancelScheduled = async (id) => {
    if (!id) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (typeof window !== 'undefined' && !window.confirm(t('admin.notifications.scheduledList.cancelConfirm'))) {
      return;
    }
    setCancellingId(id);
    try {
      await cancelScheduledPush(id);
      setScheduledItems((prev) => prev.filter((it) => it.id !== id));
      setStatus({ type: 'success', message: t('admin.notifications.scheduledList.cancelled'), data: null });
    } catch (err) {
      setStatus({ type: 'error', message: err.message || t('admin.notifications.scheduledList.cancelError'), data: null });
    } finally {
      setCancellingId(null);
    }
  };

  useEffect(() => {
    const id = window.setTimeout(() => {
      try {
        localStorage.setItem(PUSH_FORM_STORAGE_KEY, JSON.stringify(form));
      } catch {
        /* quota / private mode */
      }
    }, 350);
    return () => window.clearTimeout(id);
  }, [form]);

  useEffect(() => {
    if (!confirmOpen) setProfileIdsExpanded(false);
  }, [confirmOpen]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== PUSH_FORM_STORAGE_KEY || e.newValue == null) return;
      try {
        const parsed = JSON.parse(e.newValue);
        if (parsed && typeof parsed === 'object') {
          setForm(normalizeDraft(parsed));
        }
      } catch {
        /* ignore */
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const clientScreens = [
    { value: 'CLIENT_PENDING_SERVICES', label: t('admin.notifications.screens.clientPending') },
    { value: 'CLIENT_ACTIVE_SERVICES', label: t('admin.notifications.screens.clientActive') },
    { value: 'CLIENT_COMPLETED_SERVICES', label: t('admin.notifications.screens.clientCompleted') },
    { value: 'CLIENT_NEW_REQUEST', label: t('admin.notifications.screens.clientNewRequest') },
    { value: 'CLIENT_NOTIFICATIONS', label: t('admin.notifications.screens.clientNotifications') },
    { value: 'CLIENT_PROFILE', label: t('admin.notifications.screens.clientProfile') },
    { value: 'CLIENT_SETTINGS', label: t('admin.notifications.screens.clientSettings') }
  ];

  const providerScreens = [
    { value: 'MASTER_PURCHASED_SERVICES', label: t('admin.notifications.screens.masterPurchased') },
    { value: 'MASTER_ACTIVE_SERVICES', label: t('admin.notifications.screens.masterActive') },
    { value: 'MASTER_COMPLETED_SERVICES', label: t('admin.notifications.screens.masterCompleted') },
    { value: 'MASTER_MARKETPLACE', label: t('admin.notifications.screens.masterMarketplace') },
    { value: 'MASTER_WALLET', label: t('admin.notifications.screens.masterWallet') },
    { value: 'MASTER_CARDS', label: t('admin.notifications.screens.masterCards') },
    { value: 'MASTER_PROFILE', label: t('admin.notifications.screens.masterProfile') },
    { value: 'MASTER_PORTFOLIO', label: t('admin.notifications.screens.masterPortfolio') },
    { value: 'MASTER_REVIEWS', label: t('admin.notifications.screens.masterReviews') }
  ];

  const clearFieldError = (key) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    clearFieldError(name);
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLocalizedChange = (field, lang, value) => {
    clearFieldError(field);
    setForm((prev) => ({
      ...prev,
      [field]: { ...prev[field], [lang]: value }
    }));
  };

  const handlePlatformToggle = (platform) => {
    clearFieldError('platform');
    setForm((prev) => {
      const platforms = prev.platform.includes(platform)
        ? prev.platform.filter((p) => p !== platform)
        : [...prev.platform, platform];
      return { ...prev, platform: platforms };
    });
  };

  const handleDismissStatus = () => {
    setStatus({ type: '', message: '', data: null });
  };

  const handleClear = () => {
    setForm({
      ...DEFAULT_FORM,
      title: emptyLocalized(),
      description: emptyLocalized(),
      platform: ['ANDROID', 'IOS']
    });
    setActiveLang('ru');
    setStatus({ type: '', message: '', data: null });
    setFieldErrors({});
    setConfirmOpen(false);
    setSendMode('now');
    setScheduledSlots([]);
    setScheduleTime('');
    setScheduleError('');
  };

  const validate = () => {
    const errors = {};
    const titleMissing = LANGS.filter(({ code }) => !form.title[code].trim()).map(({ code }) => code);
    const descMissing = LANGS.filter(({ code }) => !form.description[code].trim()).map(({ code }) => code);
    if (titleMissing.length > 0) {
      errors.title = t('admin.notifications.validation.titleRequired');
      errors.titleLangs = titleMissing;
    }
    if (descMissing.length > 0) {
      errors.description = t('admin.notifications.validation.descriptionRequired');
      errors.descriptionLangs = descMissing;
    }
    if (form.targetType === 'USERS') {
      const ids = form.profileIds.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
      if (ids.length === 0) {
        errors.profileIds = t('admin.notifications.validation.profileIdsRequired');
      }
    }
    if (form.targetType === 'FILTERS' && form.platform.length === 0) {
      errors.platform = t('admin.notifications.validation.platformRequired');
    }
    return errors;
  };

  const buildPayload = (saveToDatabase) => {
    const userProfileIds =
      form.targetType === 'USERS'
        ? form.profileIds.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean)
        : [];
    const base = {
      title: {
        ru: form.title.ru.trim(),
        ro: form.title.ro.trim(),
        en: form.title.en.trim()
      },
      description: {
        ru: form.description.ru.trim(),
        ro: form.description.ro.trim(),
        en: form.description.en.trim()
      },
      imageUrl: form.imageUrl.trim(),
      targetType: form.targetType,
      userProfileIds,
      role: form.role,
      saveToDatabase,
      openPage: form.screen
    };
    if (sendMode === 'schedule' && scheduledSlots.length > 0) {
      base.scheduledAt = scheduledSlots;
    }
    if (form.targetType === 'FILTERS') {
      return { ...base, platform: form.platform };
    }
    return base;
  };

  const handleOpenConfirm = () => {
    setStatus({ type: '', message: '', data: null });
    const errors = validate();
    if (sendMode === 'schedule' && scheduledSlots.length === 0) {
      setScheduleError(t('admin.notifications.schedule.noSlots'));
      errors.schedule = true;
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSaveToDatabaseChoice(form.saveToHistory);
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    if (!loading) {
      setConfirmOpen(false);
      setStatus({ type: '', message: '', data: null });
    }
  };

  const handleConfirmSend = async () => {
    setLoading(true);
    setStatus({ type: '', message: '', data: null });
    try {
      const scheduling = sendMode === 'schedule' && scheduledSlots.length > 0;
      const payload = buildPayload(saveToDatabaseChoice);
      const response = await sendPushNotification(payload);

      if (scheduling) {
        // Ответ планирования: ApiResponse { message, data: [UUID...] }
        const count = Array.isArray(response?.data) ? response.data.length : scheduledSlots.length;
        const msg =
          (response && typeof response.message === 'string' && response.message.trim()) ||
          t('admin.notifications.schedule.scheduledOk', { count });
        setForm((prev) => ({ ...prev, saveToHistory: saveToDatabaseChoice }));
        setConfirmOpen(false);
        setCopiedFailed(false);
        setScheduledSlots([]);
        setScheduleTime('');
        setStatus({ type: 'success', message: msg, data: null });
        loadScheduled();
        return;
      }

      // New backend contract: PushSendResult { total, success, failure, failedUserIds }
      const result = (response && typeof response === 'object' && response.data && typeof response.data === 'object')
        ? response.data
        : response;
      const failedUserIds = Array.isArray(result?.failedUserIds)
        ? result.failedUserIds.filter((id) => typeof id === 'string' && id.trim()).map((id) => id.trim())
        : [];
      const stats = {
        total: Number(result?.total) || 0,
        success: Number(result?.success) || 0,
        failure: Number(result?.failure) || 0,
        failedUserIds
      };
      const msg =
        (response && typeof response.message === 'string' && response.message.trim()) ||
        t('admin.notifications.sent');
      setForm((prev) => ({ ...prev, saveToHistory: saveToDatabaseChoice }));
      setConfirmOpen(false);
      setCopiedFailed(false);
      setStatus({ type: 'success', message: msg, data: stats });
    } catch (err) {
      setStatus({ type: 'error', message: err.message || t('admin.notifications.sendError'), data: null });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyFailed = async () => {
    const ids = status.data?.failedUserIds || [];
    if (!ids.length) return;
    const text = ids.join('\n');
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedFailed(true);
      window.setTimeout(() => setCopiedFailed(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const screenOptions = form.role === 'CUSTOMER' ? clientScreens : providerScreens;
  const screenLabel = screenOptions.find((s) => s.value === form.screen)?.label ?? form.screen;
  const roleLabel =
    form.role === 'CUSTOMER'
      ? t('admin.notifications.recipient.customer')
      : t('admin.notifications.recipient.provider');
  const parsedProfileIds = form.profileIds.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
  const profileIdsSummaryText = parsedProfileIds.length ? parsedProfileIds.join(', ') : '';
  const profileIdsNeedExpandToggle =
    parsedProfileIds.length > 2 || profileIdsSummaryText.length > 110;
  const renderInPortal = (node) =>
    typeof document !== 'undefined' ? createPortal(node, document.body) : null;

  return (
    <>
      <div className="push-form-layout">
        <div className="push-form-main-column">
        {/* Main card: content + actions */}
        <motion.div className="bento-card push-form-main" style={{ padding: '0' }}>
          <div className="card-header" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '12px' }}>
              <h3
                className="card-title"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  margin: 0,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  color: 'var(--text-primary)'
                }}
              >
                <div
                  className="card-icon-circle"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'rgba(59, 130, 246, 0.12)',
                    color: '#3b82f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '-2px'
                  }}
                >
                  <Icon name="bell" size={15} />
                </div>
                {t('admin.notifications.formLabel')}
              </h3>
              <div className="push-save-header-control" title={t('admin.notifications.saveToHistory')}>
                <span className="push-save-header-label">{t('admin.notifications.saveToUserLabel')}</span>
                <label className="premium-switch push-save-header-switch" aria-label={t('admin.notifications.saveToUserLabel')}>
                  <input type="checkbox" name="saveToHistory" checked={form.saveToHistory} onChange={handleChange} />
                  <span className="switch-slider" />
                </label>
              </div>
            </div>
          </div>

          <div className="card-content-rows">
            <div className="form-row-premium">
              <div className="push-lang-tabs" role="tablist" aria-label={t('admin.notifications.localeTabsLabel')}>
                {LANGS.map(({ code }) => {
                  const titleMissing = (fieldErrors.titleLangs || []).includes(code);
                  const descMissing = (fieldErrors.descriptionLangs || []).includes(code);
                  const incomplete = titleMissing || descMissing;
                  // Точка-индикатор: зелёная, когда заголовок и текст заполнены.
                  const filled =
                    !!form.title[code].trim() && !!form.description[code].trim();
                  return (
                    <button
                      key={code}
                      type="button"
                      role="tab"
                      aria-selected={activeLang === code}
                      onClick={() => setActiveLang(code)}
                      className={`push-lang-tab ${activeLang === code ? 'active' : ''} ${incomplete ? 'push-lang-tab--error' : ''}`}
                    >
                      <span>{t(`admin.notifications.languages.${code}`)}</span>
                      <span
                        className={`push-lang-tab-dot ${incomplete ? 'error' : ''} ${filled ? 'filled' : ''}`}
                        aria-hidden
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="form-row-premium">
              <label className="row-label-caps row-label-caps--with-lang" htmlFor="push-title">
                {t('admin.notifications.titleField')}{' '}
                <span className="push-lang-suffix">
                  ({t(`admin.notifications.languages.${activeLang}`)})
                </span>
              </label>
              <input
                id="push-title"
                type="text"
                value={form.title[activeLang]}
                onChange={(e) => handleLocalizedChange('title', activeLang, e.target.value)}
                placeholder={t('admin.notifications.titlePlaceholder')}
                className={`premium-input-minimal ${(fieldErrors.titleLangs || []).includes(activeLang) ? 'premium-input-invalid' : ''}`}
                aria-invalid={(fieldErrors.titleLangs || []).includes(activeLang)}
                aria-describedby={fieldErrors.title ? 'push-title-err' : undefined}
              />
              {fieldErrors.title && (
                <p id="push-title-err" className="push-field-error" role="alert">
                  {fieldErrors.title}
                </p>
              )}
            </div>

            <div className="form-row-premium">
              <label className="row-label-caps row-label-caps--with-lang" htmlFor="push-description">
                {t('admin.notifications.description')}{' '}
                <span className="push-lang-suffix">
                  ({t(`admin.notifications.languages.${activeLang}`)})
                </span>
              </label>
              <textarea
                id="push-description"
                value={form.description[activeLang]}
                onChange={(e) => handleLocalizedChange('description', activeLang, e.target.value)}
                placeholder={t('admin.notifications.descriptionPlaceholder')}
                className={`premium-input-minimal ${(fieldErrors.descriptionLangs || []).includes(activeLang) ? 'premium-input-invalid' : ''}`}
                style={{ minHeight: '100px', resize: 'none' }}
                aria-invalid={(fieldErrors.descriptionLangs || []).includes(activeLang)}
                aria-describedby={fieldErrors.description ? 'push-desc-err' : undefined}
              />
              {fieldErrors.description && (
                <p id="push-desc-err" className="push-field-error" role="alert">
                  {fieldErrors.description}
                </p>
              )}
            </div>

            <div className="form-row-premium">
              <label className="row-label-caps">{t('admin.notifications.schedule.sectionTitle')}</label>
              <div className="push-schedule-mode">
                {[
                  { id: 'now', label: t('admin.notifications.schedule.toggleNow'), icon: 'send' },
                  { id: 'schedule', label: t('admin.notifications.schedule.toggleLater'), icon: 'clock' }
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      setScheduleError('');
                      setSendMode(opt.id);
                    }}
                    className={`push-schedule-mode-btn ${sendMode === opt.id ? 'active' : ''}`}
                  >
                    <Icon name={opt.icon} size={14} />
                    {opt.label}
                  </button>
                ))}
              </div>

              {sendMode === 'schedule' && (
                <div className="push-schedule-picker">
                  <p className="push-schedule-tz-hint">
                    <Icon name="clock" size={12} />
                    {t('admin.notifications.schedule.tzHint')}
                  </p>
                  <div className="push-schedule-inputs">
                    <div className="push-schedule-field">
                      <span className="row-label-caps">{t('admin.notifications.schedule.date')}</span>
                      <CustomDatePicker
                        selected={scheduleDate ? new Date(`${scheduleDate}T00:00:00`) : null}
                        showTimeSelect={false}
                        onChange={(date) => {
                          setScheduleError('');
                          setScheduleDate(date ? format(date, 'yyyy-MM-dd') : '');
                        }}
                      />
                    </div>
                    <div className="push-schedule-field">
                      <span className="row-label-caps">{t('admin.notifications.schedule.time')}</span>
                      <div className="custom-select-wrapper-minimal">
                        <select
                          className="minimal-select"
                          value={scheduleTime}
                          onChange={(e) => {
                            setScheduleError('');
                            setScheduleTime(e.target.value);
                          }}
                        >
                          <option value="">{t('admin.notifications.schedule.time')}</option>
                          {TIME_SLOTS.map((slot) => {
                            const past =
                              scheduleDate === todayStr &&
                              projectWallToInstant(scheduleDate, slot).getTime() <= Date.now();
                            return (
                              <option key={slot} value={slot} disabled={past}>
                                {slot}
                              </option>
                            );
                          })}
                        </select>
                        <div className="select-arrow-minimal">
                          <Icon name="chevron-down" size={12} />
                        </div>
                      </div>
                    </div>
                    <button type="button" className="push-schedule-add" onClick={addScheduleSlot}>
                      <Icon name="plus" size={14} />
                      {t('admin.notifications.schedule.add')}
                    </button>
                  </div>

                  {scheduleError && (
                    <p className="push-field-error" role="alert">
                      {scheduleError}
                    </p>
                  )}

                  {scheduledSlots.length > 0 && (
                    <div className="push-schedule-chips">
                      {scheduledSlots.map((iso) => (
                        <span key={iso} className="push-schedule-chip">
                          <Icon name="clock" size={12} />
                          {formatScheduledAt(iso)}
                          <button
                            type="button"
                            className="push-schedule-chip-remove"
                            onClick={() => removeScheduleSlot(iso)}
                            aria-label={t('admin.notifications.schedule.removeSlot')}
                          >
                            <Icon name="close" size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ padding: '24px', display: 'flex', gap: '12px' }}>
              <button type="button" onClick={handleOpenConfirm} disabled={loading} className="btn-send-push" style={{ flex: 2 }}>
                <Icon name={sendMode === 'schedule' ? 'clock' : 'send'} size={16} />
                {loading
                  ? t('admin.notifications.sending')
                  : sendMode === 'schedule'
                    ? t('admin.notifications.schedule.submit')
                    : t('admin.notifications.send')}
              </button>
              <button type="button" onClick={handleClear} className="btn-clear-push" style={{ flex: 1 }}>
                {t('common.clear')}
              </button>
            </div>
          </div>
        </motion.div>

        <AnimatePresence>
          {status.message && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="push-form-status-wrap push-form-status-wrap--under-main"
            >
              <div
                className={`push-form-status push-form-status--${status.type} push-form-status--dismissible`}
                role={status.type === 'error' ? 'alert' : 'status'}
              >
                <button
                  type="button"
                  className="push-form-status-close"
                  onClick={handleDismissStatus}
                  aria-label={t('common.close')}
                >
                  <Icon name="close" size={16} />
                </button>
                <div className="push-form-status-body">
                  <div className="push-form-status-line">{status.message}</div>
                  {status.type === 'success' && status.data && (
                    <div className="push-form-status-detail">
                      {t('admin.notifications.sendResultStats', {
                        total: status.data.total,
                        success: status.data.success,
                        failure: status.data.failure
                      })}
                    </div>
                  )}
                  {status.type === 'success' &&
                    status.data &&
                    status.data.failedUserIds &&
                    status.data.failedUserIds.length > 0 && (
                      <div className="push-failed-block">
                        <div className="push-failed-head">
                          <span className="push-failed-title">
                            {t('admin.notifications.failedTitle', {
                              count: status.data.failedUserIds.length
                            })}
                          </span>
                          <button
                            type="button"
                            className="push-failed-copy"
                            onClick={handleCopyFailed}
                          >
                            <Icon name={copiedFailed ? 'check' : 'copy'} size={13} />
                            {copiedFailed
                              ? t('admin.notifications.copied')
                              : t('admin.notifications.copyIds')}
                          </button>
                        </div>
                        <textarea
                          className="push-failed-ids"
                          readOnly
                          value={status.data.failedUserIds.join('\n')}
                          onFocus={(e) => e.target.select()}
                          aria-label={t('admin.notifications.failedTitle', {
                            count: status.data.failedUserIds.length
                          })}
                        />
                      </div>
                    )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        </div>

        {/* Technical */}
        <motion.div className="bento-card push-form-half" style={{ padding: '0' }}>
          <div className="card-header">
            <h3
              className="card-title"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                margin: 0,
                fontSize: '1.1rem',
                fontWeight: 700,
                color: 'var(--text-primary)'
              }}
            >
              <div
                className="card-icon-circle"
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  background: 'rgba(16, 185, 129, 0.1)',
                  color: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: '-2px'
                }}
              >
                <Icon name="settings" size={14} />
              </div>
              {t('admin.notifications.sectionTechnical')}
            </h3>
          </div>

          <div className="card-content-rows">
            <div className="form-row-premium">
              <label className="row-label-caps">{t('admin.notifications.targetType')}</label>
              <div className="targeting-pill-group">
                {[
                  { id: 'FILTERS', label: t('admin.notifications.byFilters') },
                  { id: 'USERS', label: t('admin.notifications.byId') }
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => {
                      clearFieldError('profileIds');
                      clearFieldError('platform');
                      setForm((prev) => ({ ...prev, targetType: opt.id }));
                    }}
                    className={`target-pill ${form.targetType === opt.id ? 'active' : ''}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {form.targetType === 'USERS' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                className="form-row-premium"
              >
                <label className="row-label-caps" htmlFor="push-profile-ids">
                  {t('admin.notifications.profileIds')}
                </label>
                <textarea
                  id="push-profile-ids"
                  name="profileIds"
                  value={form.profileIds}
                  onChange={handleChange}
                  placeholder={t('admin.notifications.profileIdsPlaceholder')}
                  className={`premium-input-minimal ${fieldErrors.profileIds ? 'premium-input-invalid' : ''}`}
                  style={{ minHeight: '80px', resize: 'none' }}
                  aria-invalid={!!fieldErrors.profileIds}
                  aria-describedby={fieldErrors.profileIds ? 'push-ids-err' : undefined}
                />
                {fieldErrors.profileIds && (
                  <p id="push-ids-err" className="push-field-error" role="alert">
                    {fieldErrors.profileIds}
                  </p>
                )}
              </motion.div>
            )}

            <div className="form-row-premium">
              <label className="row-label-caps">{t('admin.notifications.targetRole')}</label>
              <PremiumSelect
                value={form.role}
                onChange={(val) => {
                  clearFieldError('role');
                  setForm((prev) => ({
                    ...prev,
                    role: val,
                    screen: val === 'CUSTOMER' ? 'CLIENT_PENDING_SERVICES' : 'MASTER_MARKETPLACE'
                  }));
                }}
                options={[
                  { value: 'CUSTOMER', label: t('admin.notifications.recipient.customer'), icon: 'user' },
                  { value: 'PROVIDER', label: t('admin.notifications.recipient.provider'), icon: 'briefcase' }
                ]}
              />
            </div>

            <div className="form-row-premium">
              <label className="row-label-caps">{t('admin.notifications.screen')}</label>
              <PremiumSelect
                value={form.screen}
                onChange={(val) => {
                  clearFieldError('screen');
                  setForm((prev) => ({ ...prev, screen: val }));
                }}
                options={screenOptions.map((s) => ({ value: s.value, label: s.label, icon: 'layers' }))}
              />
            </div>

            {form.targetType === 'FILTERS' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                className="form-row-premium"
              >
                <label className="row-label-caps">{t('admin.notifications.platform')}</label>
                <div className="platform-toggle-group">
                  {['ANDROID', 'IOS'].map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => handlePlatformToggle(p)}
                      className={`platform-btn ${form.platform.includes(p) ? 'active' : ''}`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                {fieldErrors.platform && (
                  <p className="push-field-error" role="alert">
                    {fieldErrors.platform}
                  </p>
                )}
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Media & locale */}
        <motion.div className="bento-card push-form-half" style={{ padding: '0' }}>
          <div className="card-header">
            <h3
              className="card-title"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                margin: 0,
                fontSize: '1.1rem',
                fontWeight: 700,
                color: 'var(--text-primary)'
              }}
            >
              <div
                className="card-icon-circle"
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  background: 'rgba(168, 85, 247, 0.1)',
                  color: '#a855f7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: '-2px'
                }}
              >
                <Icon name="image" size={14} />
              </div>
              {t('admin.notifications.mediaSection')}
            </h3>
          </div>

          <div className="card-content-rows">
            <div className="form-row-premium">
              <label className="row-label-caps">{t('admin.notifications.imageUrl')}</label>
              <input
                type="text"
                name="imageUrl"
                value={form.imageUrl}
                onChange={handleChange}
                placeholder={t('admin.notifications.imageUrlPlaceholder')}
                className="premium-input-minimal"
              />
            </div>
          </div>
        </motion.div>

        {/* Scheduled notifications list */}
        <motion.div className="bento-card push-scheduled-card" style={{ padding: '0', gridColumn: '1 / -1' }}>
          <div className="card-header push-scheduled-header">
            <h3
              className="card-title"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                margin: 0,
                fontSize: '1.1rem',
                fontWeight: 700,
                color: 'var(--text-primary)'
              }}
            >
              <div
                className="card-icon-circle"
                style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  background: 'rgba(245, 158, 11, 0.12)',
                  color: '#f59e0b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  marginTop: '-2px'
                }}
              >
                <Icon name="clock" size={15} />
              </div>
              {t('admin.notifications.scheduledList.title')}
              {scheduledItems.length > 0 && (
                <span className="push-scheduled-count">{scheduledItems.length}</span>
              )}
            </h3>
            <button
              type="button"
              className="push-scheduled-refresh"
              onClick={loadScheduled}
              disabled={scheduledLoading}
            >
              <Icon name="refresh" size={14} />
              {t('admin.notifications.scheduledList.refresh')}
            </button>
          </div>

          <div className="push-scheduled-body">
            {scheduledListError && (
              <div className="push-form-status push-form-status--error" role="alert">
                {scheduledListError}
              </div>
            )}
            {scheduledLoading && scheduledItems.length === 0 ? (
              <p className="push-scheduled-empty">{t('admin.notifications.scheduledList.loading')}</p>
            ) : scheduledItems.length === 0 ? (
              <p className="push-scheduled-empty">{t('admin.notifications.scheduledList.empty')}</p>
            ) : (
              <ul className="push-scheduled-items">
                {scheduledItems.map((item) => {
                  const summary = summarizeRequestJson(item.requestJson);
                  return (
                    <li key={item.id} className="push-scheduled-item">
                      <div className="push-scheduled-item-main">
                        <div className="push-scheduled-item-when">
                          <Icon name="clock" size={13} />
                          {formatScheduledAt(item.scheduledAt)}
                        </div>
                        {(summary.title || summary.description) && (
                          <div className="push-scheduled-item-summary">
                            {summary.title && <strong>{summary.title}</strong>}
                            {summary.description && <span>{summary.description}</span>}
                          </div>
                        )}
                        <details className="push-scheduled-item-raw">
                          <summary>{t('admin.notifications.scheduledList.request')}</summary>
                          <pre>{item.requestJson}</pre>
                        </details>
                      </div>
                      <button
                        type="button"
                        className="push-scheduled-cancel"
                        onClick={() => handleCancelScheduled(item.id)}
                        disabled={cancellingId === item.id}
                      >
                        <Icon name="trash-2" size={13} />
                        {cancellingId === item.id
                          ? t('admin.notifications.scheduledList.cancelling')
                          : t('admin.notifications.scheduledList.cancel')}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </motion.div>
      </div>

      {confirmOpen &&
        renderInPortal(
        <div className="modal-overlay push-confirm-overlay push-confirm-overlay-portal" onClick={handleCloseConfirm} role="presentation">
          <div
            className="modal-content push-confirm-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="push-confirm-title"
          >
            <div className="push-confirm-scroll-area">
              <div className="modal-header push-confirm-modal-header">
                <div className="push-confirm-header-row">
                  <div className="push-confirm-header-visual">
                    <div className="push-confirm-header-icon-wrap" aria-hidden>
                      <Icon name="send" size={20} color="#ffffff" />
                    </div>
                    <div className="push-confirm-header-text">
                      <h3 id="push-confirm-title">{t('admin.notifications.confirm.title')}</h3>
                      <p className="push-confirm-subtitle">{t('admin.notifications.confirm.subtitle')}</p>
                    </div>
                  </div>
                  <div className="push-confirm-header-db">
                    <span className="push-confirm-db-label push-confirm-db-label--header">{t('admin.notifications.saveToDbLabel')}</span>
                    <label className="premium-switch push-confirm-db-switch" aria-label={t('admin.notifications.saveToDbLabel')}>
                      <input
                        type="checkbox"
                        checked={saveToDatabaseChoice}
                        onChange={(e) => setSaveToDatabaseChoice(e.target.checked)}
                      />
                      <span className="switch-slider" />
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-body push-confirm-body">
              <section className="push-confirm-section" aria-labelledby="push-confirm-section-msg">
                <div className="push-confirm-section-head" id="push-confirm-section-msg">
                  <span className="push-confirm-section-icon" aria-hidden>
                    <Icon name="bell" size={16} />
                  </span>
                  <h4 className="push-confirm-section-title">{t('admin.notifications.confirm.sectionMessage')}</h4>
                </div>
                <div className="push-confirm-message-card">
                  {LANGS.map(({ code, label }) => (
                    <div className="push-confirm-msg-block" key={code}>
                      <span className="push-confirm-lang-chip">{label}</span>
                      <span className="push-confirm-eyebrow">{t('admin.notifications.titleField')}</span>
                      <p className="push-confirm-msg-title">{form.title[code].trim() || '—'}</p>
                      <span className="push-confirm-eyebrow" style={{ marginTop: '10px' }}>
                        {t('admin.notifications.description')}
                      </span>
                      <p className="push-confirm-msg-body">{form.description[code].trim() || '—'}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="push-confirm-section" aria-labelledby="push-confirm-section-params">
                <div className="push-confirm-section-head" id="push-confirm-section-params">
                  <span className="push-confirm-section-icon" aria-hidden>
                    <Icon name="sliders" size={16} />
                  </span>
                  <h4 className="push-confirm-section-title">{t('admin.notifications.confirm.sectionParams')}</h4>
                </div>
                <div className="push-confirm-meta">
                  <div className="push-confirm-meta-row">
                    <span className="push-confirm-meta-icon" aria-hidden>
                      <Icon name="users" size={17} />
                    </span>
                    <span className="push-confirm-meta-label">{t('admin.notifications.targetType')}</span>
                    <div className="push-confirm-meta-value">
                      <span
                        className={`push-confirm-badge ${form.targetType === 'FILTERS' ? 'push-confirm-badge--filters' : 'push-confirm-badge--id'}`}
                      >
                        {form.targetType === 'USERS'
                          ? t('admin.notifications.confirm.badgeById')
                          : t('admin.notifications.confirm.badgeFilters')}
                      </span>
                    </div>
                  </div>
                  {form.targetType === 'USERS' && (
                    <div className="push-confirm-meta-row push-confirm-meta-row--stack">
                      <span className="push-confirm-meta-icon" aria-hidden>
                        <Icon name="user" size={17} />
                      </span>
                      <span className="push-confirm-meta-label">{t('admin.notifications.profileIds')}</span>
                      <div className="push-confirm-meta-value push-confirm-ids-cell">
                        {parsedProfileIds.length ? (
                          <>
                            <div
                              className={
                                profileIdsNeedExpandToggle && !profileIdsExpanded
                                  ? 'push-confirm-ids-text push-confirm-ids-text--clamped'
                                  : 'push-confirm-ids-text push-confirm-ids-text--expanded'
                              }
                            >
                              {profileIdsSummaryText}
                            </div>
                            {profileIdsNeedExpandToggle && (
                              <button
                                type="button"
                                className="push-confirm-ids-toggle"
                                onClick={() => setProfileIdsExpanded((v) => !v)}
                              >
                                {profileIdsExpanded
                                  ? t('admin.notifications.confirm.idsShowLess')
                                  : t('admin.notifications.confirm.idsShowAll')}
                              </button>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </div>
                    </div>
                  )}
                  <div className="push-confirm-meta-row">
                    <span className="push-confirm-meta-icon" aria-hidden>
                      <Icon name="briefcase" size={17} />
                    </span>
                    <span className="push-confirm-meta-label">{t('admin.notifications.targetRole')}</span>
                    <div className="push-confirm-meta-value">{roleLabel}</div>
                  </div>
                  <div className="push-confirm-meta-row">
                    <span className="push-confirm-meta-icon" aria-hidden>
                      <Icon name="layers" size={17} />
                    </span>
                    <span className="push-confirm-meta-label">{t('admin.notifications.screen')}</span>
                    <div className="push-confirm-meta-value">{screenLabel}</div>
                  </div>
                  {form.targetType === 'FILTERS' && (
                    <div className="push-confirm-meta-row">
                      <span className="push-confirm-meta-icon" aria-hidden>
                        <Icon name="phone" size={17} />
                      </span>
                      <span className="push-confirm-meta-label">{t('admin.notifications.platform')}</span>
                      <div className="push-confirm-meta-value">
                        <div className="push-confirm-pills">
                          {form.platform.map((p) => (
                            <span key={p} className="push-confirm-pill">
                              {p}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {form.imageUrl.trim() && (
                    <div className="push-confirm-meta-row push-confirm-meta-row--stack">
                      <span className="push-confirm-meta-icon" aria-hidden>
                        <Icon name="image" size={17} />
                      </span>
                      <span className="push-confirm-meta-label">{t('admin.notifications.imageUrl')}</span>
                      <div className="push-confirm-meta-value push-confirm-meta-value--url">{form.imageUrl.trim()}</div>
                    </div>
                  )}
                  {sendMode === 'schedule' && scheduledSlots.length > 0 && (
                    <div className="push-confirm-meta-row push-confirm-meta-row--stack">
                      <span className="push-confirm-meta-icon" aria-hidden>
                        <Icon name="clock" size={17} />
                      </span>
                      <span className="push-confirm-meta-label">{t('admin.notifications.schedule.sectionTitle')}</span>
                      <div className="push-confirm-meta-value push-confirm-pills">
                        {scheduledSlots.map((iso) => (
                          <span key={iso} className="push-confirm-pill">{formatScheduledAt(iso)}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {status.type === 'error' && status.message && (
                <div className="push-form-status push-form-status--error push-confirm-api-error" role="alert">
                  {status.message}
                </div>
              )}
              </div>
            </div>
            <div className="modal-actions push-confirm-actions">
              <button type="button" className="btn-clear-push" onClick={handleCloseConfirm} disabled={loading}>
                {t('common.cancel')}
              </button>
              <button type="button" className="btn-send-push" onClick={handleConfirmSend} disabled={loading}>
                {loading
                  ? t('admin.notifications.sending')
                  : sendMode === 'schedule'
                    ? t('admin.notifications.schedule.submit')
                    : t('admin.notifications.confirm.send')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
          .push-form-layout {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 24px;
            align-items: start;
            width: 100%;
          }
          .push-form-main-column {
            grid-column: 1 / -1;
            display: flex;
            flex-direction: column;
            gap: 12px;
            width: 100%;
            min-width: 0;
          }
          @media (max-width: 900px) {
            .push-form-layout {
              grid-template-columns: 1fr;
            }
            .push-form-main-column {
              grid-column: span 1;
            }
          }
          .push-field-error {
            margin: 0;
            font-size: 0.8rem;
            font-weight: 600;
            color: #ef4444;
          }
          .premium-input-invalid {
            border-color: rgba(239, 68, 68, 0.65) !important;
            box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.12);
          }
          /* Вкладки языков — в стиле маркетплейса: пилюли с точкой-индикатором */
          .push-lang-tabs {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
          }
          .push-lang-tab {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 14px;
            border-radius: 999px;
            border: 1px solid rgba(148, 163, 184, 0.25);
            background: transparent;
            color: var(--text-secondary);
            font-weight: 600;
            font-size: 0.82rem;
            cursor: pointer;
            transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
          }
          [data-theme='dark'] .push-lang-tab {
            border-color: var(--border-color);
          }
          .push-lang-tab:hover:not(.active) {
            color: var(--text-primary);
            border-color: rgba(59, 130, 246, 0.4);
          }
          .push-lang-tab.active {
            background: rgba(59, 130, 246, 0.12);
            border-color: rgba(59, 130, 246, 0.5);
            color: #3b82f6;
          }
          .push-lang-tab--error:not(.active) {
            color: #ef4444;
            border-color: rgba(239, 68, 68, 0.45);
          }
          .push-lang-tab-dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: rgba(148, 163, 184, 0.55);
          }
          .push-lang-tab-dot.filled {
            background: #22c55e;
          }
          .push-lang-tab-dot.error {
            background: #ef4444;
          }
          /* Метка поля + язык в скобках в одну строку: базовый .row-label-caps
             ограничен шириной 140px, из-за чего скобка уезжала на вторую строку. */
          .row-label-caps--with-lang {
            display: inline-flex;
            align-items: baseline;
            gap: 6px;
            width: auto;
            max-width: 100%;
            white-space: nowrap;
          }
          .push-lang-suffix {
            color: var(--text-secondary);
            font-weight: 600;
            opacity: 0.75;
          }
          .push-failed-block {
            margin-top: 10px;
            padding-top: 10px;
            border-top: 1px solid rgba(239, 68, 68, 0.22);
            text-align: left;
          }
          .push-failed-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            margin-bottom: 6px;
          }
          .push-failed-title {
            font-size: 0.78rem;
            font-weight: 700;
            color: #ef4444;
          }
          .push-failed-copy {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            padding: 5px 10px;
            border-radius: 7px;
            border: 1px solid rgba(239, 68, 68, 0.35);
            background: rgba(239, 68, 68, 0.08);
            color: #ef4444;
            font-size: 0.72rem;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.15s ease;
            flex-shrink: 0;
          }
          .push-failed-copy:hover {
            background: rgba(239, 68, 68, 0.15);
          }
          .push-failed-ids {
            width: 100%;
            min-height: 64px;
            max-height: 160px;
            resize: vertical;
            padding: 8px 10px;
            border-radius: 8px;
            border: 1px solid rgba(226, 232, 240, 0.9);
            background: rgba(255, 255, 255, 0.85);
            color: var(--text-primary);
            font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
            font-size: 0.78rem;
            line-height: 1.5;
            outline: none;
          }
          [data-theme='dark'] .push-failed-ids {
            background: rgba(15, 23, 42, 0.5);
            border-color: rgba(148, 163, 184, 0.25);
          }
          .push-confirm-lang-chip {
            display: inline-flex;
            align-items: center;
            padding: 2px 8px;
            margin-bottom: 8px;
            border-radius: 6px;
            background: rgba(59, 130, 246, 0.1);
            color: #2563eb;
            font-size: 0.68rem;
            font-weight: 800;
            letter-spacing: 0.05em;
          }
          [data-theme='dark'] .push-confirm-lang-chip {
            background: rgba(96, 165, 250, 0.15);
            color: #93c5fd;
          }
          .push-form-status-wrap--under-main {
            margin-top: 0;
            width: 100%;
          }
          .push-form-status {
            position: relative;
            padding: 12px 40px 12px 16px;
            border-radius: 10px;
            font-size: 0.85rem;
            font-weight: 600;
            text-align: center;
            border: 1px solid transparent;
          }
          .push-form-status-close {
            position: absolute;
            top: 8px;
            right: 8px;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border: none;
            border-radius: 8px;
            background: transparent;
            color: inherit;
            opacity: 0.55;
            cursor: pointer;
            transition: opacity 0.15s ease, background 0.15s ease;
          }
          .push-form-status-close:hover {
            opacity: 1;
            background: rgba(15, 23, 42, 0.06);
          }
          .push-form-status--error .push-form-status-close:hover {
            background: rgba(239, 68, 68, 0.12);
          }
          .push-form-status--success .push-form-status-close:hover {
            background: rgba(16, 185, 129, 0.15);
          }
          .push-form-status-body {
            text-align: center;
          }
          .push-form-status-line {
            line-height: 1.45;
          }
          .push-form-status-detail {
            margin-top: 8px;
            padding-top: 8px;
            border-top: 1px solid rgba(16, 185, 129, 0.22);
            font-size: 0.78rem;
            font-weight: 600;
            line-height: 1.45;
            opacity: 0.95;
          }
          .push-form-status--success {
            background: rgba(16, 185, 129, 0.1);
            color: #10b981;
            border-color: rgba(16, 185, 129, 0.2);
          }
          .push-form-status--error {
            background: rgba(239, 68, 68, 0.1);
            color: #ef4444;
            border-color: rgba(239, 68, 68, 0.2);
          }
          .modal-overlay.push-confirm-overlay {
            background: rgba(15, 23, 42, 0.78);
            backdrop-filter: none;
            -webkit-backdrop-filter: none;
          }
          .modal-overlay.push-confirm-overlay.push-confirm-overlay-portal {
            position: fixed !important;
            inset: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            z-index: 3200 !important;
          }
          .push-confirm-modal.modal-content {
            display: flex;
            flex-direction: column;
            max-width: 540px;
            width: 92%;
            max-height: min(88vh, 720px);
            border-radius: 18px;
            background: #ffffff !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
            opacity: 1;
            box-shadow: 0 24px 64px rgba(0, 0, 0, 0.28);
            overflow: hidden;
          }
          .push-confirm-scroll-area {
            flex: 1 1 auto;
            min-height: 0;
            overflow-x: hidden;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }
          [data-theme='dark'] .push-confirm-modal.modal-content {
            background: #1e293b !important;
            border-color: rgba(148, 163, 184, 0.35);
          }
          .push-confirm-modal .modal-body,
          .push-confirm-modal .modal-actions {
            background: transparent;
          }
          .push-confirm-modal-header.modal-header {
            position: sticky;
            top: 0;
            z-index: 5;
            padding: 18px 22px 20px;
            border-radius: 18px 18px 0 0;
            overflow: hidden;
            background: rgba(241, 245, 249, 0.68);
            backdrop-filter: saturate(1.15) blur(14px);
            -webkit-backdrop-filter: saturate(1.15) blur(14px);
            border-bottom: 1px solid rgba(15, 23, 42, 0.07);
            box-shadow: 0 8px 24px -12px rgba(15, 23, 42, 0.12);
          }
          [data-theme='dark'] .push-confirm-modal-header.modal-header {
            background: rgba(15, 23, 42, 0.72);
            backdrop-filter: saturate(1.1) blur(16px);
            -webkit-backdrop-filter: saturate(1.1) blur(16px);
            border-bottom-color: rgba(148, 163, 184, 0.14);
            box-shadow: 0 10px 28px -14px rgba(0, 0, 0, 0.35);
          }
          .push-confirm-header-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 20px;
            flex-wrap: wrap;
          }
          .push-confirm-header-visual {
            display: flex;
            align-items: center;
            gap: 14px;
            min-width: 0;
            flex: 1 1 220px;
          }
          .push-confirm-header-db {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-shrink: 0;
            padding: 6px 0;
          }
          .push-confirm-db-label--header {
            font-size: 0.8rem;
            font-weight: 600;
            color: #475569;
            text-align: right;
            line-height: 1.3;
            max-width: 140px;
          }
          [data-theme='dark'] .push-confirm-db-label--header {
            color: #94a3b8;
          }
          @media (max-width: 520px) {
            .push-confirm-header-db {
              flex: 1 1 100%;
              justify-content: space-between;
              padding-top: 4px;
              border-top: 1px solid rgba(148, 163, 184, 0.25);
              margin-top: 2px;
            }
            .push-confirm-db-label--header {
              text-align: left;
              max-width: none;
            }
          }
          .push-confirm-header-icon-wrap {
            width: 44px;
            height: 44px;
            border-radius: 10px;
            background: #1e40af;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            border: 1px solid #1e3a8a;
            box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
          }
          [data-theme='dark'] .push-confirm-header-icon-wrap {
            background: #1d4ed8;
            border-color: #2563eb;
            box-shadow: none;
          }
          .push-confirm-header-text {
            min-width: 0;
            flex: 1;
          }
          .push-confirm-modal-header h3 {
            margin: 0 0 4px;
            font-size: 1.05rem;
            font-weight: 700;
            letter-spacing: -0.015em;
            color: #0f172a;
            line-height: 1.3;
          }
          [data-theme='dark'] .push-confirm-modal-header h3 {
            color: #f1f5f9;
          }
          .push-confirm-subtitle {
            margin: 0;
            font-size: 0.8125rem;
            color: #64748b;
            line-height: 1.5;
            max-width: 42em;
          }
          [data-theme='dark'] .push-confirm-subtitle {
            color: #94a3b8;
          }
          .push-confirm-body {
            padding: 16px 22px 18px;
          }
          .push-confirm-section {
            margin-bottom: 18px;
          }
          .push-confirm-section:last-of-type {
            margin-bottom: 0;
          }
          .push-confirm-section-head {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 10px;
          }
          .push-confirm-section-icon {
            width: 28px;
            height: 28px;
            border-radius: 8px;
            background: rgba(59, 130, 246, 0.1);
            color: #3b82f6;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          [data-theme='dark'] .push-confirm-section-icon {
            background: rgba(96, 165, 250, 0.15);
            color: #93c5fd;
          }
          .push-confirm-section-title {
            margin: 0;
            font-size: 0.78rem;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: var(--text-secondary);
          }
          .push-confirm-message-card {
            border-radius: 14px;
            padding: 14px 16px;
            background: linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.65) 100%);
            border: 1px solid rgba(226, 232, 240, 0.95);
            box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
          }
          [data-theme='dark'] .push-confirm-message-card {
            background: rgba(15, 23, 42, 0.55);
            border-color: rgba(148, 163, 184, 0.22);
            box-shadow: none;
          }
          .push-confirm-msg-block + .push-confirm-msg-block {
            margin-top: 14px;
            padding-top: 14px;
            border-top: 1px solid rgba(203, 213, 225, 0.7);
          }
          [data-theme='dark'] .push-confirm-msg-block + .push-confirm-msg-block {
            border-top-color: rgba(148, 163, 184, 0.2);
          }
          .push-confirm-eyebrow {
            display: block;
            font-size: 0.68rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: var(--text-secondary);
            margin-bottom: 6px;
            opacity: 0.9;
          }
          .push-confirm-msg-title {
            margin: 0;
            font-size: 1.05rem;
            font-weight: 800;
            color: var(--text-primary);
            line-height: 1.35;
            word-break: break-word;
          }
          .push-confirm-msg-body {
            margin: 0;
            font-size: 0.92rem;
            font-weight: 500;
            color: var(--text-primary);
            line-height: 1.55;
            white-space: pre-wrap;
            word-break: break-word;
          }
          .push-confirm-meta {
            border-radius: 14px;
            border: 1px solid rgba(226, 232, 240, 0.9);
            background: rgba(255, 255, 255, 0.6);
            overflow: hidden;
          }
          [data-theme='dark'] .push-confirm-meta {
            border-color: rgba(148, 163, 184, 0.22);
            background: rgba(15, 23, 42, 0.35);
          }
          .push-confirm-meta-row {
            display: grid;
            grid-template-columns: 22px minmax(100px, 38%) 1fr;
            gap: 10px 12px;
            align-items: center;
            padding: 11px 14px;
            border-bottom: 1px solid rgba(241, 245, 249, 0.95);
            font-size: 0.88rem;
          }
          [data-theme='dark'] .push-confirm-meta-row {
            border-bottom-color: rgba(51, 65, 85, 0.6);
          }
          .push-confirm-meta-row:last-child {
            border-bottom: none;
          }
          .push-confirm-meta-row--stack {
            align-items: start;
            grid-template-columns: 22px 1fr;
            grid-template-rows: auto auto;
          }
          .push-confirm-meta-row--stack .push-confirm-meta-icon {
            grid-row: 1;
            grid-column: 1;
            margin-top: 2px;
          }
          .push-confirm-meta-row--stack .push-confirm-meta-label {
            grid-row: 1;
            grid-column: 2;
          }
          .push-confirm-meta-row--stack .push-confirm-meta-value {
            grid-row: 2;
            grid-column: 1 / -1;
            margin-left: 34px;
            margin-top: 6px;
            text-align: left;
          }
          .push-confirm-meta-icon {
            color: var(--text-secondary);
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0.85;
          }
          .push-confirm-meta-label {
            font-weight: 700;
            color: var(--text-secondary);
            font-size: 0.78rem;
            line-height: 1.3;
          }
          .push-confirm-meta-value {
            font-weight: 600;
            color: var(--text-primary);
            text-align: right;
            word-break: break-word;
            min-width: 0;
          }
          .push-confirm-meta-value--multiline {
            text-align: left;
            white-space: pre-wrap;
            font-size: 0.82rem;
            font-weight: 600;
            line-height: 1.45;
          }
          .push-confirm-meta-value--url {
            text-align: left;
            font-size: 0.78rem;
            font-weight: 500;
            word-break: break-all;
            color: #2563eb;
          }
          [data-theme='dark'] .push-confirm-meta-value--url {
            color: #93c5fd;
          }
          .push-confirm-ids-cell {
            text-align: left;
          }
          .push-confirm-ids-text {
            font-size: 0.82rem;
            font-weight: 600;
            line-height: 1.45;
            word-break: break-all;
            color: var(--text-primary);
          }
          .push-confirm-ids-text--clamped {
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3;
            overflow: hidden;
          }
          .push-confirm-ids-text--expanded {
            max-height: min(38vh, 220px);
            overflow-y: auto;
            padding-right: 2px;
          }
          .push-confirm-ids-toggle {
            display: block;
            margin-top: 5px;
            padding: 0;
            border: none;
            background: transparent;
            font-size: 0.68rem;
            font-weight: 700;
            letter-spacing: 0.02em;
            color: #64748b;
            cursor: pointer;
            text-decoration: underline;
            text-underline-offset: 2px;
          }
          .push-confirm-ids-toggle:hover {
            color: #1d4ed8;
          }
          [data-theme='dark'] .push-confirm-ids-toggle {
            color: #94a3b8;
          }
          [data-theme='dark'] .push-confirm-ids-toggle:hover {
            color: #93c5fd;
          }
          .push-confirm-badge {
            display: inline-flex;
            align-items: center;
            padding: 4px 10px;
            border-radius: 999px;
            font-size: 0.72rem;
            font-weight: 800;
            letter-spacing: 0.03em;
            text-transform: uppercase;
          }
          .push-confirm-badge--filters {
            background: rgba(16, 185, 129, 0.12);
            color: #059669;
            border: 1px solid rgba(16, 185, 129, 0.25);
          }
          .push-confirm-badge--id {
            background: rgba(51, 65, 85, 0.1);
            color: #334155;
            border: 1px solid rgba(51, 65, 85, 0.22);
          }
          [data-theme='dark'] .push-confirm-badge--filters {
            background: rgba(52, 211, 153, 0.15);
            color: #6ee7b7;
            border-color: rgba(52, 211, 153, 0.25);
          }
          [data-theme='dark'] .push-confirm-badge--id {
            background: rgba(148, 163, 184, 0.12);
            color: #cbd5e1;
            border-color: rgba(148, 163, 184, 0.28);
          }
          .push-confirm-pills {
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            justify-content: flex-end;
          }
          .push-confirm-pill {
            padding: 4px 10px;
            border-radius: 8px;
            font-size: 0.7rem;
            font-weight: 800;
            letter-spacing: 0.04em;
            background: rgba(59, 130, 246, 0.1);
            color: #1d4ed8;
            border: 1px solid rgba(59, 130, 246, 0.22);
          }
          [data-theme='dark'] .push-confirm-pill {
            background: rgba(96, 165, 250, 0.15);
            color: #bfdbfe;
            border-color: rgba(96, 165, 250, 0.28);
          }
          .push-confirm-db-switch {
            flex-shrink: 0;
          }
          .push-confirm-api-error {
            margin-top: 12px;
            text-align: left;
          }
          .push-confirm-actions {
            flex-shrink: 0;
            padding: 16px 22px 20px;
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            border-top: 1px solid var(--border-color);
            background: rgba(248, 250, 252, 0.5);
          }
          [data-theme='dark'] .push-confirm-actions {
            background: rgba(15, 23, 42, 0.4);
          }
          .push-confirm-actions .btn-clear-push,
          .push-confirm-actions .btn-send-push {
            flex: 0 0 auto;
            min-width: 128px;
          }
          .card-content-rows {
            display: flex;
            flex-direction: column;
          }
          .form-row-premium {
            padding: 16px 24px;
            border-bottom: none;
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          .form-row-multi {
            padding: 16px 24px;
            border-bottom: none;
            display: flex;
            gap: 24px;
            align-items: flex-start;
          }
          .form-col {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          .col-divider-v {
            width: 1px;
            align-self: stretch;
            border-left: 1px solid rgba(148, 163, 184, 0.18);
            margin: 4px 0;
          }
          .form-row-premium:last-child, .form-row-multi:last-child {
            border-bottom: none;
          }
          .row-label-caps {
            font-size: 0.72rem;
            color: var(--text-secondary);
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }
          .premium-input-minimal {
            background: rgba(248, 250, 252, 0.5);
            border: 1px solid rgba(226, 232, 240, 0.8);
            padding: 10px 14px;
            border-radius: 8px;
            width: 100%;
            font-size: 0.95rem;
            color: var(--text-primary);
            font-weight: 500;
            outline: none;
            transition: all 0.2s ease;
          }
          .premium-input-minimal:hover {
            background: rgba(248, 250, 252, 0.8);
            border-color: #cbd5e1;
          }
          .premium-input-minimal:focus {
            background: white;
            border-color: var(--accent-primary);
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
          }
          .push-save-header-control {
            display: flex;
            align-items: center;
            gap: 10px;
            flex-shrink: 0;
            max-width: min(240px, 42%);
          }
          @media (max-width: 640px) {
            .push-save-header-control {
              max-width: none;
              flex: 1;
              justify-content: flex-end;
            }
          }
          .push-save-header-label {
            font-size: 0.8rem;
            font-weight: 600;
            color: var(--text-secondary);
            line-height: 1.3;
            text-align: right;
          }
          [data-theme='dark'] .push-save-header-label {
            color: rgba(226, 232, 240, 0.85);
          }
          .premium-switch {
            position: relative;
            display: inline-block;
            width: 40px;
            height: 22px;
            flex-shrink: 0;
          }
          .premium-switch input { opacity: 0; width: 0; height: 0; }
          .switch-slider {
            position: absolute;
            cursor: pointer;
            top: 0; left: 0; right: 0; bottom: 0;
            background-color: #e2e8f0;
            transition: .3s cubic-bezier(0.4, 0, 0.2, 1);
            border-radius: 34px;
          }
          .switch-slider:before {
            position: absolute;
            content: "";
            height: 16px; width: 16px;
            left: 3px; bottom: 3px;
            background-color: white;
            transition: .3s cubic-bezier(0.4, 0, 0.2, 1);
            border-radius: 50%;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          input:checked + .switch-slider { background-color: var(--accent-primary); }
          input:focus + .switch-slider { box-shadow: 0 0 1px var(--accent-primary); }
          input:checked + .switch-slider:before { transform: translateX(18px); }

          .btn-send-push {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            padding: 12px;
            border-radius: 10px;
            background: var(--accent-primary);
            color: white;
            border: none;
            font-weight: 700;
            font-size: 0.95rem;
            cursor: pointer;
            transition: all 0.2s ease;
            box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
          }
          .btn-send-push:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 6px 15px rgba(37, 99, 235, 0.3);
            background: var(--accent-hover);
          }
          .btn-clear-push {
            padding: 12px;
            border-radius: 10px;
            background: white;
            color: var(--text-secondary);
            border: 1px solid var(--border-color);
            font-weight: 600;
            font-size: 0.95rem;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          .btn-clear-push:hover:not(:disabled) {
            background: #f8fafc;
            color: var(--text-primary);
            border-color: #cbd5e1;
          }
          .btn-send-push:disabled, .btn-clear-push:disabled {
            opacity: 0.65;
            cursor: not-allowed;
          }
          .targeting-pill-group {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
          }
          .target-pill {
            padding: 6px 16px;
            border-radius: 20px;
            border: 1px solid var(--border-color);
            background: white;
            color: var(--text-secondary);
            font-weight: 600;
            font-size: 0.85rem;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          .target-pill.active {
            background: var(--accent-primary);
            color: white;
            border-color: var(--accent-primary);
          }
          .platform-toggle-group {
            display: flex;
            gap: 8px;
          }
          .platform-btn {
            flex: 1;
            padding: 8px;
            border-radius: 8px;
            border: 1px solid var(--border-color);
            background: white;
            color: var(--text-secondary);
            font-weight: 700;
            font-size: 0.85rem;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          .platform-btn.active {
            background: rgba(59, 130, 246, 0.1);
            border-color: var(--accent-primary);
            color: var(--accent-primary);
          }
          .custom-select-wrapper-minimal {
            position: relative;
            width: 100%;
          }
          .minimal-select {
            width: 100%;
            appearance: none;
            background: rgba(248, 250, 252, 0.5);
            border: 1px solid rgba(226, 232, 240, 0.8);
            padding: 10px 30px 10px 14px;
            border-radius: 8px;
            font-size: 0.95rem;
            font-weight: 500;
            color: var(--text-primary);
            outline: none;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          .minimal-select:hover { border-color: #cbd5e1; }
          .select-arrow-minimal {
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            pointer-events: none;
            color: var(--text-secondary);
          }

          /* Тёмная тема: кнопка «Очистить», выпадающие списки и «таблетки»
             использовали хардкод background: white и оставались светлыми. */
          [data-theme='dark'] .btn-clear-push {
            background: var(--bg-tertiary);
            color: var(--text-secondary);
            border-color: var(--border-color);
          }
          [data-theme='dark'] .btn-clear-push:hover:not(:disabled) {
            background: var(--bg-secondary);
            color: var(--text-primary);
            border-color: var(--accent-hover);
          }
          [data-theme='dark'] .minimal-select {
            background: var(--bg-tertiary);
            border-color: var(--border-color);
            color: var(--text-primary);
            /* нативный список опций рисуется ОС — переключаем его в тёмную схему */
            color-scheme: dark;
          }
          [data-theme='dark'] .minimal-select:hover { border-color: var(--accent-hover); }
          [data-theme='dark'] .target-pill,
          [data-theme='dark'] .platform-btn {
            background: var(--bg-tertiary);
            border-color: var(--border-color);
            color: var(--text-secondary);
          }
          [data-theme='dark'] .target-pill.active {
            background: var(--accent-primary);
            color: #fff;
            border-color: var(--accent-primary);
          }
          [data-theme='dark'] .platform-btn.active {
            background: rgba(59, 130, 246, 0.18);
            border-color: var(--accent-primary);
            color: var(--accent-hover);
          }

          /* ---- Scheduling ---- */
          .push-schedule-mode {
            display: flex;
            gap: 8px;
          }
          .push-schedule-mode-btn {
            flex: 1;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            padding: 9px 12px;
            border-radius: 10px;
            border: 1px solid var(--border-color);
            background: white;
            color: var(--text-secondary);
            font-weight: 600;
            font-size: 0.88rem;
            cursor: pointer;
            transition: all 0.18s ease;
          }
          .push-schedule-mode-btn:hover:not(.active) {
            border-color: #cbd5e1;
            color: var(--text-primary);
          }
          .push-schedule-mode-btn.active {
            background: rgba(59, 130, 246, 0.1);
            border-color: var(--accent-primary);
            color: var(--accent-primary);
          }
          [data-theme='dark'] .push-schedule-mode-btn {
            background: rgba(15, 23, 42, 0.4);
          }
          .push-schedule-picker {
            margin-top: 12px;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }
          .push-schedule-tz-hint {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            margin: 0;
            font-size: 0.78rem;
            font-weight: 600;
            color: var(--text-secondary);
          }
          .push-schedule-inputs {
            display: flex;
            gap: 12px;
            align-items: flex-end;
            flex-wrap: wrap;
          }
          .push-schedule-field {
            display: flex;
            flex-direction: column;
            gap: 6px;
            flex: 1;
            min-width: 140px;
          }
          .push-schedule-add {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 10px 16px;
            border-radius: 8px;
            border: 1px solid var(--accent-primary);
            background: var(--accent-primary);
            color: #fff;
            font-weight: 700;
            font-size: 0.85rem;
            cursor: pointer;
            transition: all 0.18s ease;
            white-space: nowrap;
            height: 42px;
          }
          .push-schedule-add:hover {
            background: var(--accent-hover);
          }
          .push-schedule-chips {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
          }
          .push-schedule-chip {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 8px 6px 12px;
            border-radius: 999px;
            background: rgba(245, 158, 11, 0.12);
            color: #b45309;
            border: 1px solid rgba(245, 158, 11, 0.3);
            font-size: 0.8rem;
            font-weight: 700;
          }
          [data-theme='dark'] .push-schedule-chip {
            background: rgba(245, 158, 11, 0.16);
            color: #fcd34d;
            border-color: rgba(245, 158, 11, 0.3);
          }
          .push-schedule-chip-remove {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 18px;
            height: 18px;
            border: none;
            border-radius: 50%;
            background: rgba(180, 83, 9, 0.14);
            color: inherit;
            cursor: pointer;
            padding: 0;
          }
          .push-schedule-chip-remove:hover {
            background: rgba(180, 83, 9, 0.28);
          }

          /* ---- Scheduled list card ---- */
          .push-scheduled-card {
            width: 100%;
          }
          .push-scheduled-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
          }
          .push-scheduled-count {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 22px;
            height: 22px;
            padding: 0 7px;
            margin-left: 8px;
            border-radius: 999px;
            background: rgba(245, 158, 11, 0.16);
            color: #b45309;
            font-size: 0.75rem;
            font-weight: 800;
          }
          [data-theme='dark'] .push-scheduled-count {
            color: #fcd34d;
          }
          .push-scheduled-refresh {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 7px 12px;
            border-radius: 8px;
            border: 1px solid var(--border-color);
            background: white;
            color: var(--text-secondary);
            font-weight: 600;
            font-size: 0.82rem;
            cursor: pointer;
            transition: all 0.18s ease;
            flex-shrink: 0;
          }
          .push-scheduled-refresh:hover:not(:disabled) {
            border-color: #cbd5e1;
            color: var(--text-primary);
          }
          .push-scheduled-refresh:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
          [data-theme='dark'] .push-scheduled-refresh {
            background: rgba(15, 23, 42, 0.4);
          }
          .push-scheduled-body {
            padding: 16px 24px 20px;
          }
          .push-scheduled-empty {
            margin: 0;
            padding: 18px 0;
            text-align: center;
            color: var(--text-secondary);
            font-size: 0.9rem;
          }
          .push-scheduled-items {
            list-style: none;
            margin: 0;
            padding: 0;
            display: flex;
            flex-direction: column;
            gap: 10px;
          }
          .push-scheduled-item {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 14px;
            padding: 14px 16px;
            border-radius: 12px;
            border: 1px solid rgba(226, 232, 240, 0.9);
            background: rgba(248, 250, 252, 0.6);
          }
          [data-theme='dark'] .push-scheduled-item {
            border-color: rgba(148, 163, 184, 0.22);
            background: rgba(15, 23, 42, 0.35);
          }
          .push-scheduled-item-main {
            min-width: 0;
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          .push-scheduled-item-when {
            display: inline-flex;
            align-items: center;
            gap: 7px;
            font-weight: 800;
            font-size: 0.92rem;
            color: var(--text-primary);
          }
          .push-scheduled-item-summary {
            display: flex;
            flex-direction: column;
            gap: 2px;
            font-size: 0.85rem;
            color: var(--text-secondary);
            word-break: break-word;
          }
          .push-scheduled-item-summary strong {
            color: var(--text-primary);
            font-weight: 700;
          }
          .push-scheduled-item-raw summary {
            cursor: pointer;
            font-size: 0.75rem;
            font-weight: 700;
            color: var(--accent-primary);
            list-style: revert;
          }
          .push-scheduled-item-raw pre {
            margin: 8px 0 0;
            padding: 10px 12px;
            border-radius: 8px;
            background: rgba(15, 23, 42, 0.05);
            color: var(--text-primary);
            font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
            font-size: 0.72rem;
            line-height: 1.5;
            white-space: pre-wrap;
            word-break: break-word;
            max-height: 200px;
            overflow: auto;
          }
          [data-theme='dark'] .push-scheduled-item-raw pre {
            background: rgba(15, 23, 42, 0.6);
          }
          .push-scheduled-cancel {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 8px 12px;
            border-radius: 8px;
            border: 1px solid rgba(239, 68, 68, 0.35);
            background: rgba(239, 68, 68, 0.08);
            color: #ef4444;
            font-weight: 700;
            font-size: 0.8rem;
            cursor: pointer;
            transition: all 0.15s ease;
            flex-shrink: 0;
          }
          .push-scheduled-cancel:hover:not(:disabled) {
            background: rgba(239, 68, 68, 0.16);
          }
          .push-scheduled-cancel:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
          @media (max-width: 560px) {
            .push-scheduled-item {
              flex-direction: column;
            }
            .push-scheduled-cancel {
              align-self: flex-end;
            }
          }
        `}</style>
    </>
  );
};

export default AdminPushForm;
